import { z } from "zod";
import type { Operation } from "../types";

/**
 * Objectives — the strategic-pulse wire contract (Measured Business F3 / #1173,
 * docs/MASTER-PRD.md Part 10 U1/U2). `GET /api/objectives` returns the workspace's
 * objectives (optionally scoped to ONE property via `projectSlug`) each with its
 * key results + the derived attainment, health (0..100), and band, plus the
 * strategic "are we on plan?" summary the L0 hero shows above the function-health
 * wall. The consumer is the `<ObjectivesHero>` block; the route is
 * `createRoute(objectives.summary, …)` over `listObjectives`/`summarizeObjectives`
 * from `@excellent/core`.
 *
 * LOCKED IA: an objective is NOT a separate wall — a tracked objective (◎) is a
 * metric with a committed target, so "are we on plan?" === "is this bar above its
 * benchmark?". Properties are a drill DIMENSION (the `projectSlug` scope), never a
 * top-level flip.
 */

export const OBJECTIVE_BANDS = ["ok", "watch", "risk", "pending"] as const;
export type ObjectiveBandDTO = (typeof OBJECTIVE_BANDS)[number];

export const objectiveKeyResult = z
  .object({
    uid: z.string(),
    title: z.string(),
    metric: z.string().nullable(),
    unit: z.string().nullable(),
    direction: z.string(),
    startValue: z.number().nullable(),
    targetValue: z.number().nullable(),
    currentValue: z.number().nullable(),
    /** Direction-aware 0..1, null when not measurable (a ghost, never a fake 0).
     *  When `signalRef` is bound, this reads the live pulse health of that cluster
     *  (one number, two verdicts) instead of the typed `currentValue`. */
    attainment: z.number().nullable(),
    /** The bound pulse touchpoint `<mode>.<groupKey>` (v91), or null when manual. */
    signalRef: z.string().nullable().optional(),
  })
  .passthrough();
export type ObjectiveKeyResultDTO = z.infer<typeof objectiveKeyResult>;

export const objectiveDTO = z
  .object({
    uid: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    period: z.string().nullable(),
    status: z.string(),
    /** Links an excellent_goal (never forks it). */
    goalUid: z.string().nullable(),
    // Property attribution — which project/property owns this objective.
    projectId: z.number().nullable(),
    projectSlug: z.string().nullable(),
    projectName: z.string().nullable(),
    dueOn: z.string().nullable(),
    /** Mean KR attainment 0..1, null when no KR is measurable. */
    attainment: z.number().nullable(),
    /** attainment × 100, rounded; null when not measurable. */
    health: z.number().nullable(),
    band: z.enum(OBJECTIVE_BANDS),
    /** The "go deeper" target for this objective bar. */
    href: z.string(),
    keyResults: z.array(objectiveKeyResult),
  })
  .passthrough();
export type ObjectiveDTO = z.infer<typeof objectiveDTO>;

export const objectiveSummaryDTO = z.object({
  total: z.number().int(),
  /** band === "ok" */
  onTrack: z.number().int(),
  watch: z.number().int(),
  risk: z.number().int(),
  /** band === "pending" — tracked but no measurable KR yet (awaiting its spine). */
  pending: z.number().int(),
});
export type ObjectiveSummaryDTO = z.infer<typeof objectiveSummaryDTO>;

const summaryQuery = z.object({
  /** Scope to ONE property (project slug). Omit for the whole business. */
  projectSlug: z.string().min(1).optional(),
  /** Filter by objective status (e.g. "active"). Omit for all. */
  status: z.string().min(1).optional(),
});
type SummaryQuery = z.infer<typeof summaryQuery>;

const summaryResponse = z.object({
  objectives: z.array(objectiveDTO),
  summary: objectiveSummaryDTO,
  /** The resolved property scope (null = whole business). */
  scope: z.object({
    projectSlug: z.string().nullable(),
    projectName: z.string().nullable(),
  }),
  /** Goals (uid + title) the create/edit form can LINK an objective to — an
   *  objective references an excellent_goal (goal_uid), never forks it. */
  goalOptions: z.array(z.object({ uid: z.string(), title: z.string() })),
});
type SummaryResponse = z.infer<typeof summaryResponse>;

const summaryOp: Operation<void, SummaryQuery, void, SummaryResponse> = {
  method: "GET",
  path: () => "/api/objectives",
  query: summaryQuery,
  response: summaryResponse,
};

// ── Mutations (RO-2-09 — ported from `excellent/src/agents/objective-actions.ts`,
// a `"use server"` file with no route.ts equivalent). Writes are direct
// `@excellent/core` CRUD (the objective tables aren't an event-engine seam), so
// the handler bodies port verbatim. Every op's response is a discriminated
// `{ok:true,...}|{ok:false,reason}` union carried as the envelope's `data` —
// same pattern `agent-goals.ts`'s `create` op already uses — because a
// validation failure ("title required") is a normal result, not a transport
// error; only `apiCall`'s outer `{ok,data}|{ok,error}` envelope should throw. */

export type ObjectiveMutateResult =
  | { ok: true; uid: string }
  | { ok: false; reason: string };

const objectiveCreateBody = z.object({
  title: z.string(),
  description: z.string().nullable().optional(),
  period: z.string().nullable().optional(),
  dueOn: z.string().nullable().optional(),
  /** Property (project slug) this objective belongs to. */
  projectSlug: z.string().nullable().optional(),
  /** Executable goal (uid) this objective links to (never forks it). */
  goalUid: z.string().nullable().optional(),
});
export type ObjectiveCreateBody = z.infer<typeof objectiveCreateBody>;

const createObjectiveOp: Operation<void, void, ObjectiveCreateBody, ObjectiveMutateResult> = {
  method: "POST",
  path: () => "/api/objectives",
  body: objectiveCreateBody,
  response: z.custom<ObjectiveMutateResult>(),
  invalidates: [summaryOp],
};

export type ObjectiveUpdateResult = { ok: boolean; reason?: string };

const objectiveUpdateBody = z.object({
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  period: z.string().nullable().optional(),
  dueOn: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  goalUid: z.string().nullable().optional(),
});
export type ObjectiveUpdateBody = z.infer<typeof objectiveUpdateBody>;

const updateObjectiveOp: Operation<
  { uid: string },
  void,
  ObjectiveUpdateBody,
  ObjectiveUpdateResult
> = {
  method: "PATCH",
  path: ({ uid }) => `/api/objectives/${uid}`,
  params: z.object({ uid: z.string().min(1) }),
  body: objectiveUpdateBody,
  response: z.custom<ObjectiveUpdateResult>(),
  invalidates: [summaryOp],
};

const keyResultCreateBody = z.object({
  objectiveUid: z.string().min(1),
  title: z.string(),
  /** Bind the KR to a metric key — joins live pulse data so its current value
   *  can be read without a manual entry. */
  metric: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  direction: z.string().nullable().optional(),
  startValue: z.union([z.number(), z.string()]).nullable().optional(),
  targetValue: z.union([z.number(), z.string()]).nullable().optional(),
  currentValue: z.union([z.number(), z.string()]).nullable().optional(),
  signalRef: z.string().nullable().optional(),
});
export type KeyResultCreateBody = z.infer<typeof keyResultCreateBody>;

const createKeyResultOp: Operation<void, void, KeyResultCreateBody, ObjectiveUpdateResult> = {
  method: "POST",
  path: () => "/api/key-results",
  body: keyResultCreateBody,
  response: z.custom<ObjectiveUpdateResult>(),
  // KRs are embedded on the objective summary payload.
  invalidates: [summaryOp],
};

const keyResultUpdateBody = z.object({
  title: z.string().optional(),
  metric: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  direction: z.string().nullable().optional(),
  startValue: z.union([z.number(), z.string()]).nullable().optional(),
  targetValue: z.union([z.number(), z.string()]).nullable().optional(),
  currentValue: z.union([z.number(), z.string()]).nullable().optional(),
  status: z.string().nullable().optional(),
  signalRef: z.string().nullable().optional(),
});
export type KeyResultUpdateBody = z.infer<typeof keyResultUpdateBody>;

const updateKeyResultOp: Operation<
  { uid: string },
  void,
  KeyResultUpdateBody,
  ObjectiveUpdateResult
> = {
  method: "PATCH",
  path: ({ uid }) => `/api/key-results/${uid}`,
  params: z.object({ uid: z.string().min(1) }),
  body: keyResultUpdateBody,
  response: z.custom<ObjectiveUpdateResult>(),
  invalidates: [summaryOp],
};

export const objectives = {
  summary: summaryOp,
  create: createObjectiveOp,
  update: updateObjectiveOp,
  createKeyResult: createKeyResultOp,
  updateKeyResult: updateKeyResultOp,
};
